import { FC, useEffect } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { fetchLocations } from "../../../store/locationStore/locationThunk";

// Reusable Location picker backed by the locationStore.
interface Props {
  value?: any; // locationId or location object
  onChange: (locationId: string) => void;
  label?: string;
  activeOnly?: boolean;
  size?: "small" | "medium";
  // When true, renders as a borderless "c-text-field-3" pill (matches the Items filter bar).
  borderless?: boolean;
  width?: number | string;
  // Location id to hide from the list (e.g. exclude the transfer source from the
  // destination picker so the same location can't be both).
  excludeId?: string;
}

const LocationSelect: FC<Props> = ({ value, onChange, label = "Location", activeOnly = true, size = "small", borderless = false, width, excludeId }) => {
  const dispatch = useAppDispatch();
  const { locations } = useAppSelector((state) => state.locations);

  useEffect(() => {
    if (!locations || locations.length === 0) dispatch(fetchLocations());
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const options = (locations || [])
    .filter((l) => (activeOnly ? l.isActive !== false : true))
    .filter((l) => (excludeId ? l._id !== excludeId : true))
    .map((l) => ({ label: `${l.name} (${l.code})`, value: l._id }));

  const currentId = value?._id || value || "";
  const selected = options.find((o) => o.value === currentId) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(o: any) => o.label || ""}
      isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
      value={selected}
      onChange={(_e, v: any) => onChange(v ? v.value : "")}
      style={width ? { width } : undefined}
      renderInput={(params) =>
        borderless ? (
          <TextField
            {...params}
            className="c-text-field-3"
            variant="standard"
            size={size}
            placeholder={label}
            InputProps={{ ...params.InputProps, disableUnderline: true, style: { fontSize: 14 } }}
          />
        ) : (
          <TextField {...params} label={label} size={size} />
        )
      }
    />
  );
};

export default LocationSelect;
